/**
 * Skill file-tree primitives shared by hub-as-code and org-as-code.
 *
 * Both surfaces represent a skill's files as a directory tree on disk
 * (`<root>/skills/<skill_name>/...`), scan them into `HubAsCodeResourceFile[]` (text inline,
 * binary as base64, SHA-256 hashes), and write them back. Extracted here so
 * the binary/hash/orphan-cleanup rules live in exactly one place.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as crypto from 'node:crypto';
import type { HubAsCodeAgent as WireHubAsCodeAgent, HubAsCodePayload as WireHubAsCodePayload, OrgResourcesPayload } from '@wayai/core/contracts/ci';
import { SKILL_NAMESPACE, skillSlug } from '@wayai/core/resource-paths';
import type { HubAsCodeAgent, HubAsCodePayload, HubAsCodeSkill, HubAsCodeResourceFile, OrgSkillsPayload } from './types.js';
import { writeFileNoFollow, ensureRealSubdirNoSymlink, fileHasBytes, requireRealSubdirNoSymlink, symlinkRefusal, type FileChangeLog } from './fs-safety.js';
import { MAX_RESOURCE_AGGREGATE_ENCODED_BYTES } from '@wayai/core/contracts';
import { expected } from './expected.js';
import { slugify } from './utils.js';

/** The folder a hub's or an organization's skills live in, beside `hub.yaml` or `skills.yaml`. */
export const SKILLS_DIR = SKILL_NAMESPACE;

/**
 * The folder a pre-skills CLI wrote them to (`resources/<slugified name>/`), with `resources:` in
 * `hub.yaml` (or `org/resources.yaml`). Push refuses a folder still holding it; pull converts it
 * (wayai-pro/wayai#5142 step 5 D1b).
 */
export const OLD_RESOURCES_DIR = 'resources';

/**
 * A skill's folder name: its `skill_name`, else its name, slugged — the one rule its `skills/`
 * mount takes, so `skills/<folder>/…` on disk is the path an agent reads (`skillSlug`).
 */
export function skillFolder(skill: Pick<HubAsCodeSkill, 'name' | 'skill_name'>): string {
  return skillSlug({ resource_name: skill.name, skill_name: skill.skill_name });
}

/**
 * Refuse a skills list in which two skills map to one folder (one's `skill_name` the other's, or its
 * name slugged): they would share `skills/<folder>/`, a pull writing one over the other and a push
 * sending each with the other's files. The platform's mount tells them apart by a `-2` it ranks by
 * creation time, which a pulled payload does not carry, so the CLI refuses instead of guessing.
 */
export function assertDistinctSkillFolders(skills: Array<Pick<HubAsCodeSkill, 'name' | 'skill_name'>>): void {
  const byFolder = new Map<string, string>();
  for (const skill of skills) {
    const folder = skillFolder(skill);
    const other = byFolder.get(folder);
    if (other !== undefined) {
      throw expected(
        `Skills "${other}" and "${skill.name}" both map to ${SKILLS_DIR}/${folder}/: each skill needs its own folder. `
          + 'Give one of them a different skill_name (in the console, or in the config file and its folder), then try again.',
      );
    }
    byFolder.set(folder, skill.name);
  }
}

/** The folder a pre-skills CLI wrote a skill to, under `resources/`: its name, slugged. */
export function oldResourceFolder(skill: Pick<HubAsCodeSkill, 'name'>): string {
  return slugify(skill.name);
}

/**
 * Refuse a `type` on a skill entry: a skill takes none. `type: skill` (an older pull's) is read as
 * the skill it is; anything else, `type: knowledge` included, is refused rather than pushed as a
 * skill (knowledge resources are retired; documents live in the knowledge base).
 */
export function assertSkillEntryType(name: string, type: unknown): void {
  if (type === undefined || type === 'skill') return;
  throw expected(`Skill "${name}" has \`type: ${String(type)}\`: a skill takes no type. Documents live in the knowledge base (\`wayai files\`).`);
}

/**
 * Copy an entry's `enabled` from a file onto what is sent. Silence means on, so `true` goes unsent. Anything
 * else goes, for the platform to refuse what is not `false`: `enabled: no` is a string in YAML, and dropped
 * here it would read as on.
 */
export function carryEnabled(written: unknown, target: { enabled?: boolean }): void {
  if (written !== undefined && written !== true) target.enabled = written as boolean;
}

/** `obj` with key `from` renamed `to` in place (same position), its value mapped by `map`. */
function renameKey<T extends object>(obj: T, from: string, to: string, map: (value: unknown) => unknown): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === from) out[to] = map(value);
    else out[key] = value;
  }
  return out;
}

/** A pulled resource entry (the server's or an old folder's) as a skill: `type` dropped. */
export function skillFromResource<R extends { type?: unknown }>(resource: R): Omit<R, 'type'> {
  const { type: _type, ...skill } = resource;
  return skill;
}

/**
 * An agent with its `resources:` links (the server's pull answer, or an old folder's agent file)
 * as `skills:` links naming each skill by `skill_id`, in the same place among its keys.
 */
export function agentWithSkillLinks<A extends object>(agent: A): A {
  if (!('resources' in agent)) return agent;
  return renameKey(agent, 'resources', 'skills', (links) => Array.isArray(links)
    ? links.map((link) => (link && typeof link === 'object' && 'resource_id' in link
      ? renameKey(link, 'resource_id', 'skill_id', (id) => id)
      : link))
    : links) as A;
}

/** `payload` with its `resources` section as `skills`, each entry a skill (in the same place). */
function withSkillsSection(payload: object): Record<string, unknown> {
  if (!('resources' in payload)) return payload as Record<string, unknown>;
  return renameKey(payload, 'resources', 'skills', (resources) => Array.isArray(resources)
    ? resources.map((resource) => skillFromResource(resource as { type?: unknown }))
    : resources);
}

/** The server's hub pull answer (`resources`, agents' `resources:` links) as the CLI's skills. */
export function hubPayloadFromPull(wire: WireHubAsCodePayload): HubAsCodePayload {
  const payload = withSkillsSection(wire);
  if (Array.isArray(payload.agents)) {
    payload.agents = (payload.agents as WireHubAsCodeAgent[]).map((agent) => agentWithSkillLinks(agent) as unknown as HubAsCodeAgent);
  }
  return payload as unknown as HubAsCodePayload;
}

/** The server's organization pull answer (`resources`) as the CLI's skills. */
export function orgPayloadFromPull(wire: OrgResourcesPayload): OrgSkillsPayload {
  return withSkillsSection(wire) as unknown as OrgSkillsPayload;
}

/** The command a refusal of a pull's conversion names. */
export type PullCommand = 'wayai pull' | 'wayai org pull';

/** The entries of `root/<sub>`, or none when it is absent — never listed through a symlink. */
function listEntries(root: string, sub: string): fs.Dirent[] {
  const dir = path.join(root, sub);
  try {
    fs.lstatSync(dir);
  } catch {
    return [];
  }
  // The conversion deletes what this lists: never through a symlinked `resources/` (or root).
  requireRealSubdirNoSymlink(root, dir, false);
  return fs.readdirSync(dir, { withFileTypes: true });
}

/**
 * Pull's conversion of an old folder (wayai-pro/wayai#5142 step 5 D1b): the `resources/<slug>`
 * folders a pre-skills CLI wrote for the skills `skills` lists, which pull removes once it has
 * written them under `skills/`, and what else the conversion would remove or overwrite unasked
 * (`leftover`): every other entry of `resources/`, and, while `root` is still in the old layout
 * (`oldLayout`), every entry of `skills/` — no CLI managed `skills/` there, so what it holds is the
 * user's, and the pull never compared it (it read the old folder). Pull refuses (`assert…`) before writing anything
 * while any is left: it may be a knowledge backup, which a pull never deletes. `root` is the hub
 * folder or the org folder.
 */
export function oldLayoutEntries(
  root: string,
  skills: Array<Pick<HubAsCodeSkill, 'name' | 'skill_name'>>,
  oldLayout: boolean,
): { converted: string[]; leftover: string[] } {
  const oldFolders = new Set(skills.map(oldResourceFolder));
  const converted: string[] = [];
  const leftover: string[] = [];
  for (const entry of listEntries(root, OLD_RESOURCES_DIR)) {
    if (entry.isDirectory() && oldFolders.has(entry.name)) converted.push(entry.name);
    else leftover.push(`${OLD_RESOURCES_DIR}/${entry.name}`);
  }
  if (oldLayout) {
    for (const entry of listEntries(root, SKILLS_DIR)) leftover.push(`${SKILLS_DIR}/${entry.name}`);
  }
  return { converted, leftover };
}

/** Refuse, before any write, a pull whose conversion would remove or overwrite what it never compared. */
export function assertOldLayoutConvertible(
  root: string,
  skills: Array<Pick<HubAsCodeSkill, 'name' | 'skill_name'>>,
  oldLayout: boolean,
  pullCommand: PullCommand,
): void {
  const { leftover } = oldLayoutEntries(root, skills, oldLayout);
  if (leftover.length === 0) return;
  const one = leftover.length === 1;
  throw expected(
    `${leftover.sort().join(', ')} would be removed or overwritten unseen. Converting a folder an older CLI wrote, `
      + `\`${pullCommand}\` writes ${SKILLS_DIR}/ from the platform and removes from ${OLD_RESOURCES_DIR}/ only the folders `
      + `named after the skills it writes. Move ${one ? 'it' : 'them'} out of ${path.basename(root)}/ (a knowledge backup `
      + `belongs outside the workspace), then run \`${pullCommand}\` again. Nothing was written.`,
  );
}

/**
 * Remove the `resources/<slug>` folders of the skills just written under `skills/`, then
 * `resources/` once empty. Called after `assertOldLayoutConvertible`, so nothing else is there —
 * or, with `all` (a read-only production mirror, the CLI's own copy), everything in it. Each
 * removed entry is recorded as `resources/<name>/`.
 */
export function removeConvertedOldResources(
  root: string,
  skills: Array<Pick<HubAsCodeSkill, 'name' | 'skill_name'>>,
  log?: FileChangeLog,
  opts: { all?: boolean } = {},
): void {
  const dir = path.join(root, OLD_RESOURCES_DIR);
  const names = opts.all
    ? listEntries(root, OLD_RESOURCES_DIR).map((entry) => entry.name)
    : oldLayoutEntries(root, skills, false).converted;
  for (const name of names) {
    fs.rmSync(path.join(dir, name), { recursive: true, force: true });
    log?.removed.push(`${OLD_RESOURCES_DIR}/${name}/`);
  }
  try {
    if (fs.readdirSync(dir).length === 0) fs.rmdirSync(dir);
  } catch { /* absent */ }
}

export const BINARY_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico',
  'mp3', 'mp4', 'wav', 'zip', 'tar', 'gz',
  'woff', 'woff2', 'ttf', 'eot',
]);

export const MAX_RESOURCE_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function isBinaryFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return BINARY_EXTENSIONS.has(ext);
}

export function guessMimeType(filename: string): string | undefined {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const mimeMap: Record<string, string> = {
    md: 'text/markdown', txt: 'text/plain', json: 'application/json',
    yaml: 'text/yaml', yml: 'text/yaml', html: 'text/html', css: 'text/css',
    js: 'text/javascript', ts: 'text/typescript', xml: 'text/xml', csv: 'text/csv',
    pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', ico: 'image/x-icon',
    mp3: 'audio/mpeg', mp4: 'video/mp4', wav: 'audio/wav',
    zip: 'application/zip', tar: 'application/x-tar', gz: 'application/gzip',
    woff: 'font/woff', woff2: 'font/woff2', ttf: 'font/ttf', eot: 'application/vnd.ms-fontobject',
  };
  return mimeMap[ext];
}

export function computeHash(data: Uint8Array): string {
  return crypto.createHash('sha256').update(data).digest('hex');
}

/**
 * Recursively scan a resource directory into file entries (text inline, binary base64). The
 * result is uploaded, so a symlink anywhere in the tree is refused — reading it would send
 * the link target, and skipping it would drop the file from the desired state (the server
 * then deletes it). The caller checks `dir` itself.
 */
/**
 * A file over the size limit is skipped; `onOversized` decides what that means (a warning by
 * default). `parseHubFolder` passes its own, which `wayai pull` turns into a refusal.
 */
export function scanResourceFiles(
  dir: string,
  prefix = '',
  onOversized: (relPath: string, reason: string) => void = (relPath, reason) => {
    console.warn(`  Warning: skipping ${relPath} (${reason})`);
  },
): HubAsCodeResourceFile[] {
  const files: HubAsCodeResourceFile[] = [];
  const entries = fs.readdirSync(dir, { withFileTypes: true });

  for (const entry of entries) {
    const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;

    if (entry.isSymbolicLink()) {
      throw symlinkRefusal(relPath, 'file or folder');
    } else if (entry.isDirectory()) {
      files.push(...scanResourceFiles(path.join(dir, entry.name), relPath, onOversized));
    } else if (entry.isFile()) {
      const fullPath = path.join(dir, entry.name);
      const stat = fs.statSync(fullPath);

      if (stat.size > MAX_RESOURCE_FILE_SIZE) {
        onOversized(relPath, `${(stat.size / 1024 / 1024).toFixed(1)}MB exceeds 10MB limit`);
        continue;
      }

      const fileEntry: HubAsCodeResourceFile = {
        path: relPath,
        mime_type: guessMimeType(entry.name),
        file_size: stat.size,
      };

      const data = fs.readFileSync(fullPath);
      fileEntry.hash = computeHash(data);

      if (isBinaryFile(entry.name)) {
        fileEntry.content_base64 = data.toString('base64');
      } else {
        fileEntry.content = data.toString('utf-8');
      }

      files.push(fileEntry);
    }
  }

  return files;
}

/**
 * Remove byte carriers when the target hub already stores the same hash. Path +
 * hash remain in the declarative payload, so the backend matches the file before
 * its deletion pass and skips it as unchanged (gh #3401).
 */
export function omitUnchangedResourceFileContent(
  localResources: HubAsCodeSkill[] | undefined,
  remoteResources: HubAsCodeSkill[] | undefined,
): number {
  if (!localResources?.length || !remoteResources?.length) return 0;

  const remoteById = new Map(remoteResources.flatMap((resource) => resource.id ? [[resource.id, resource] as const] : []));
  const remoteByName = new Map(remoteResources.map((resource) => [resource.name, resource] as const));
  let omitted = 0;

  for (const localResource of localResources) {
    const remoteResource = (localResource.id ? remoteById.get(localResource.id) : undefined)
      ?? remoteByName.get(localResource.name);
    if (!remoteResource?.files?.length || !localResource.files?.length) continue;

    const remoteFilesById = new Map(remoteResource.files.flatMap((file) => file.id ? [[file.id, file] as const] : []));
    const remoteFilesByPath = new Map(remoteResource.files.map((file) => [file.path, file] as const));
    for (const localFile of localResource.files) {
      const remoteFile = (localFile.id ? remoteFilesById.get(localFile.id) : undefined)
        ?? remoteFilesByPath.get(localFile.path);
      if (!localFile.hash || !remoteFile?.hash || localFile.hash !== remoteFile.hash) continue;

      if (localFile.content !== undefined) {
        delete localFile.content;
        omitted++;
      }
      if (localFile.content_base64 !== undefined) {
        delete localFile.content_base64;
        omitted++;
      }
    }
  }

  return omitted;
}

/** Build an independent wire payload whose unchanged skill files carry only metadata. */
export function prepareResourcePayload(
  local: HubAsCodePayload,
  remote: HubAsCodePayload,
): HubAsCodePayload {
  const payload = structuredClone(local);
  omitUnchangedResourceFileContent(payload.skills, remote.skills);
  return payload;
}

/** A complete fallback payload is valid only while its inline carriers fit the wire contract. */
export function assertResourcePayloadFitsWithoutPruning(payload: HubAsCodePayload, cause: unknown): void {
  let bytes = 0;
  for (const skill of payload.skills ?? []) {
    for (const file of skill.files ?? []) {
      bytes += file.content_base64?.length ?? 0;
      bytes += file.content === undefined ? 0 : Buffer.byteLength(file.content, 'utf8');
      if (bytes > MAX_RESOURCE_AGGREGATE_ENCODED_BYTES) {
        throw new Error('Could not compare these large skill files because remote hashes are unavailable; retry the command.', { cause });
      }
    }
  }
}

/**
 * Write a resource's files into `resDir`, then prune orphans. Text and base64
 * content land synchronously; files carrying only `download_url` are left for
 * the caller's async download pass but still counted as written (not orphaned).
 *
 * Two-level containment (gh #3365): the TRUSTED `root` (hub/org folder) bounds `resDir`
 * itself — a symlinked or ancestor-symlinked `skills/<skill_name>` that escapes the workspace
 * is rejected — while the REAL `resDir` bounds each per-file write, so a nested subdir
 * symlink can't redirect a write ELSEWHERE inside the workspace (e.g. `<res>/docs -> hub/`
 * overwriting `hub.yaml`). A symlinked resDir can't be its own boundary because `root` is.
 */
export function writeResourceFileTree(resDir: string, files: HubAsCodeResourceFile[], root: string, log?: FileChangeLog): void {
  // resDir must be reachable from the trusted workspace `root` through NO symlinked component
  // (catches a symlinked resDir, a symlinked ANCESTOR like `skills/` — even when resDir
  // doesn't exist yet — and inward-pointing root symlinks). Create it (real dirs) when writing.
  if (!ensureRealSubdirNoSymlink(root, resDir, files.length > 0)) {
    console.warn(`  Warning: skipping skill folder (not reachable inside the workspace without a symlink)`);
    return;
  }
  if (files.length === 0) {
    if (fs.existsSync(resDir)) cleanOrphanFiles(resDir, '', new Set<string>(), root, log);
    return;
  }

  const writtenPaths = new Set<string>();
  for (const file of files) {
    const filePath = path.join(resDir, file.path);
    // Guard against path traversal (e.g. "../../../.bashrc")
    if (!filePath.startsWith(resDir + path.sep) && filePath !== resDir) {
      console.warn(`  Warning: skipping ${file.path} (path traversal detected)`);
      continue;
    }
    writtenPaths.add(file.path);

    // download_url files carry no inline bytes here — they're fetched later by
    // downloadBinaryFiles (still reserved above so they aren't orphan-pruned).
    const buf = file.content !== undefined
      ? Buffer.from(file.content, 'utf-8')
      : file.content_base64
        ? Buffer.from(file.content_base64, 'base64')
        : undefined;
    if (buf === undefined) continue;

    // Idempotent: skip a byte-identical write (no mtime churn); the file stays reserved in
    // writtenPaths so it isn't orphan-pruned. fileHasBytes is lstat-guarded, so a symlinked
    // destination reads as "not identical" and falls through to the no-follow write below.
    if (fileHasBytes(filePath, buf)) continue;

    // writeFileNoFollow verifies the parent dir resolves inside `resDir` and never follows a
    // symlinked destination — a `skills/docs/x.pdf -> /tmp/victim` link or a `docs -> hub/`
    // subdir symlink must not redirect the write out of the resource folder (gh #3365).
    if (writeFileNoFollow(resDir, filePath, buf)) {
      log?.changed.push(path.relative(root, filePath));
    } else {
      console.warn(`  Warning: skipping ${file.path} (destination escapes the skill folder via a symlink)`);
      writtenPaths.delete(file.path);
    }
  }

  if (fs.existsSync(resDir)) cleanOrphanFiles(resDir, '', writtenPaths, root, log);
}

/**
 * Recursively remove files not in `writtenPaths`, plus any directories left empty. When
 * `root` + `log` are provided, each removed FILE is recorded (hub-relative) into `log.removed`;
 * empty-dir tidy-up rmdirs are not recorded (they hold no user content). Callers that only
 * prune (no announcement) may omit both.
 */
export function cleanOrphanFiles(dir: string, prefix: string, writtenPaths: Set<string>, root?: string, log?: FileChangeLog): void {
  const entries = fs.readdirSync(dir, { withFileTypes: true });

  for (const entry of entries) {
    const relPath = prefix ? `${prefix}/${entry.name}` : entry.name;
    const fullPath = path.join(dir, entry.name);

    if (entry.isDirectory()) {
      cleanOrphanFiles(fullPath, relPath, writtenPaths, root, log);
      if (fs.readdirSync(fullPath).length === 0) fs.rmdirSync(fullPath);
    } else if (!writtenPaths.has(relPath)) {
      fs.unlinkSync(fullPath);
      if (log && root) log.removed.push(path.relative(root, fullPath));
    }
  }
}

/**
 * Download binary files (those carrying `download_url` and no inline content)
 * into `resDir`. Returns the count downloaded. Path-traversal guarded.
 *
 * Two-level containment (gh #3365): the TRUSTED `root` (hub/org folder) bounds `resDir`
 * itself; the REAL `resDir` bounds each per-file write (so a nested subdir symlink can't
 * redirect a write elsewhere in the workspace).
 */
export async function downloadBinaryFiles(resDir: string, files: HubAsCodeResourceFile[], root: string, log?: FileChangeLog): Promise<number> {
  // resDir must be reachable from the trusted workspace `root` through no symlinked component
  // (incl. a symlinked ancestor when resDir doesn't exist yet). Create it real; write below is
  // then bounded by resDir.
  if (!ensureRealSubdirNoSymlink(root, resDir, true)) {
    console.warn(`  Warning: skipping skill file downloads (folder not reachable inside the workspace without a symlink)`);
    return 0;
  }
  let count = 0;
  for (const file of files) {
    if (!file.download_url || file.content !== undefined) continue;
    try {
      const filePath = path.join(resDir, file.path);
      if (!filePath.startsWith(resDir + path.sep) && filePath !== resDir) {
        console.warn(`  Warning: skipping ${file.path} (path traversal detected)`);
        continue;
      }
      // Idempotent: skip when the local REAL file already matches by content hash — keeps repeat
      // pulls cheap and keeps the change log honest. lstat: never hash through a symlink; a
      // symlinked destination falls through to the no-follow write below.
      if (file.hash) {
        try {
          const st = fs.lstatSync(filePath);
          if (st.isFile() && !st.isSymbolicLink() && computeHash(fs.readFileSync(filePath)) === file.hash) continue;
        } catch { /* absent → download */ }
      }
      const response = await fetch(file.download_url);
      if (!response.ok) {
        console.warn(`  Warning: failed to download ${file.path} (${response.status})`);
        continue;
      }
      const buffer = Buffer.from(await response.arrayBuffer());
      // Idempotent for hashless rows too (backend `file_hash` may be null): the hash-skip above
      // can't fire, so compare the fetched bytes against the local real file and skip the write
      // when they already match — no mtime churn, no false `changed` entry on a repeat pull.
      // lstat-guarded (a symlinked destination reads as "not identical" → falls through to the
      // no-follow write below).
      if (fileHasBytes(filePath, buffer)) continue;
      // No-follow write bounded by the REAL resource dir: refuse a symlinked destination or a
      // nested subdir symlink that redirects out of resDir (even back into the workspace).
      if (writeFileNoFollow(resDir, filePath, buffer)) {
        count++;
        log?.changed.push(path.relative(root, filePath));
      } else {
        console.warn(`  Warning: skipping ${file.path} (destination escapes the skill folder via a symlink)`);
      }
    } catch (err) {
      console.warn(`  Warning: failed to download ${file.path} (${err instanceof Error ? err.message : String(err)})`);
    }
  }
  return count;
}
